import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoPlayer({ url, poster, resumeFrom = 0, onProgress }) {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const lastSaved = useRef(0);
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;
    setLoading(true);
    setError(false);
    lastSaved.current = 0;
    let hls;
    const isHls = url.includes(".m3u8") || url.includes("/live/") || !/\.(mp4|mkv|avi|mov|webm|ts)(\?|$)/i.test(url);
    const proxied = `${API}/stream?url=${encodeURIComponent(url)}`;

    const onReady = () => setLoading(false);
    const onMeta = () => { if (resumeFrom > 0 && isFinite(video.duration)) { try { video.currentTime = resumeFrom; } catch (e) {} } };
    const report = () => {
      if (progressRef.current && video.currentTime > 0) {
        progressRef.current(video.currentTime, isFinite(video.duration) ? video.duration : 0);
      }
    };
    const onTime = () => {
      if (video.currentTime - lastSaved.current >= 10) { lastSaved.current = video.currentTime; report(); }
    };
    video.addEventListener("playing", onReady);
    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("pause", report);
    video.addEventListener("ended", report);

    if (isHls && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: true, manifestLoadingTimeOut: 20000 });
      hls.loadSource(proxied);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data.fatal) { setError(true); setLoading(false); } });
    } else {
      video.src = proxied;
      video.play().catch(() => {});
      video.addEventListener("error", () => { setError(true); setLoading(false); });
    }

    return () => {
      report();
      video.removeEventListener("playing", onReady);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("pause", report);
      video.removeEventListener("ended", report);
      if (hls) hls.destroy();
    };
  }, [url]);

  return (
    <div className="relative w-full h-full bg-black rounded-lg overflow-hidden" data-testid="video-player">
      <video ref={videoRef} controls autoPlay playsInline poster={poster}
        className="w-full h-full object-contain bg-black" />
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 className="w-10 h-10 text-red-500 animate-spin" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#050505]/90 text-center px-6" data-testid="player-error">
          <AlertTriangle className="w-10 h-10 text-amber-400" />
          <p className="text-zinc-300 max-w-sm">This channel/content cannot be played in the browser (possible CORS/geo-block on the source).</p>
        </div>
      )}
    </div>
  );
}
